import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { euro } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { Logo } from "../components/Logo";

const MAX_POLLS = 8;

export default function PaymentResult() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isCancel = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "verifying");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let polls = 0;
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") { setState("success"); return; }
      } catch { /* ignore */ }
      polls += 1;
      if (polls >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(check, 1800);
    };
    check();
    return () => clearTimeout(timer);
  }, [isCancel, sessionId]);

  const config = {
    verifying: { icon: Loader2, spin: true, color: "text-gold", title: t("paymentVerifying"), sub: t("paymentPendingSub") },
    success: { icon: CheckCircle2, color: "text-emerald-400", title: t("paymentSuccess"), sub: t("paymentSuccessSub") },
    pending: { icon: Loader2, spin: true, color: "text-amber-400", title: t("paymentPending"), sub: t("paymentPendingSub") },
    cancelled: { icon: XCircle, color: "text-red-400", title: t("paymentCancelled"), sub: t("paymentCancelledSub") },
  }[state];
  const Icon = config.icon;

  return (
    <div className="relative grid min-h-screen place-items-center bg-ink px-4 text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <div className="relative w-full max-w-md rounded-3xl border border-cardline bg-card2/70 p-8 text-center backdrop-blur-xl" data-testid="payment-result">
        <Logo size={30} className="mx-auto justify-center" />
        <Icon size={56} className={`mx-auto mt-6 ${config.color} ${config.spin ? "animate-spin" : ""}`} data-testid={`payment-${state}`} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold">{config.title}</h1>
        <p className="mt-2 text-sm text-slate-400">{config.sub}</p>
        {tx && state === "success" && (
          <div className="mt-5 rounded-2xl border border-cardline bg-ink2/50 p-4 text-left text-sm">
            <div className="flex justify-between"><span className="text-slate-400">{tx.creator_name}</span><span className="font-bold text-gold">{euro(tx.amount)}</span></div>
          </div>
        )}
        <button
          onClick={() => navigate("/campaigns")}
          data-testid="payment-back-btn"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-3 font-bold text-ink"
        >
          <ArrowLeft size={18} /> {t("backToCampaigns")}
        </button>
      </div>
    </div>
  );
}
